import { Redirect } from 'next';
import React from 'react';
import { useTranslation } from 'next-i18next';

import ContactPageComponent, {
  ContactPageProps,
} from '../components/ContactPage';
import Metas, { LocalizedAlternate, MetasProps } from '../components/Metas';
import useChangeActiveLocale from '../hooks/useChangeActiveLocale';
import JSONSafe from '../lib/utils/json';
import { validSupportedLocale } from '../lib/utils/locale';
import * as ReverseRoutingHelpers from '../lib/utils/reverse-routing';
import HelpCenterInstanceProvider from '../providers/HelpCenterInstance';
import { withDynamic404 } from '../wrappers/dynamic-404';
import {
  getHelpCenterChromeServerSideProps,
  getHelpCenterCommonServerSideProps,
  getHelpCenterSearchServerSideProps,
  HelpCenterCommonProps,
} from '../wrappers/help-center';
import withCommonProcessing from '../wrappers/withCommonProcessing';

type ContactProps = ContactPageProps & HelpCenterCommonProps;

const ContactPage = ({ helpCenter, metas, ...props }: ContactProps) => {
  const { t } = useTranslation();
  const changeActiveLocale = useChangeActiveLocale();
  return (
    <HelpCenterInstanceProvider helpCenter={helpCenter}>
      <Metas
        title={t('contactPageMetaTitle')}
        description={
          metas.description ??
          t('contactPageMetaDescription', { helpCenterName: helpCenter.name })
        }
        {...metas}
      />
      <ContactPageComponent
        {...props}
        onChangeActiveLocale={changeActiveLocale}
      />
    </HelpCenterInstanceProvider>
  );
};

export const getServerSideProps = withCommonProcessing<HelpCenterCommonProps>(
  async ({ client, helpCenter, locale, host }) => {
    if (
      !(
        helpCenter.contactFormEmailIntegrationId &&
        helpCenter.isContactFormCardEnabled
      )
    ) {
      const redirect: Redirect = {
        destination: ReverseRoutingHelpers.getPathToHomePage(),
        statusCode: 302,
      };

      return {
        redirect,
      };
    }

    // Retrieve locales and meta
    const localizedAlternates: LocalizedAlternate[] =
      helpCenter.supportedLocales.map((supportedLocale) => ({
        locale: supportedLocale,
        url: ReverseRoutingHelpers.getAbsoluteURL({
          host,
          locale: validSupportedLocale(supportedLocale),
          path: ReverseRoutingHelpers.getPathToHomePage(),
        }),
      }));

    const localizedDefaultURL = ReverseRoutingHelpers.getAbsoluteURL({
      host,
      locale: validSupportedLocale(helpCenter.defaultLocale),
      path: ReverseRoutingHelpers.getPathToHomePage(),
    });

    const canonicalURL = ReverseRoutingHelpers.getAbsoluteURL({
      host,
      locale,
      path: ReverseRoutingHelpers.getPathToHomePage(),
    });

    const metas: Partial<MetasProps> = {
      localizedAlternates,
      localizedDefaultURL,
      canonicalURL,
    };

    return {
      props: JSONSafe({
        ...(await getHelpCenterChromeServerSideProps(
          client,
          helpCenter,
          locale,
        )),
        ...(await getHelpCenterCommonServerSideProps(
          helpCenter,
          locale,
          metas,
        )),
        ...getHelpCenterSearchServerSideProps(helpCenter),
        helpCenterId: helpCenter.id,
        subjectLines: helpCenter.allSubjectLines?.[locale] ?? null,
      }),
    };
  },
);

export default withDynamic404(ContactPage);
